import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import api from '../../../../utils/api';
import { AsyncAutocomplete } from '../../../common/AsyncAutocomplete';
import DatePickerField from '../../../common/DatePickerField';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';

interface Option {
  value: string;
  label: string;
}

export interface BasicEntry {
  vacancyType: string;
  vacancyTitle: string;
  vacancyCode: string;
  minIndustryExperience: string;
  startDate: string | null;
  endDate: string | null;
  vacancyReason: string;
  designation: Option | null;
  isActive: boolean;
}

interface VacancyBasicCardProps {
  type: 'add' | 'edit' | 'view';
  basicData: BasicEntry;
  setBasicData: (data: BasicEntry) => void;
}

const VacancyBasicCard: React.FC<VacancyBasicCardProps> = ({ type, basicData, setBasicData }) => {
  const vacancyTypeOptions = [
    { value: 'TAAS', label: 'TAAS' },
    { value: 'EOR', label: 'EOR' },
  ];

  const handleLoadDesignations = async (query: string) => {
    const response = await api.get<{ data: { id: number; designation: string }[] }>(
      `/rec/vacancy/designations?query=${query}`,
      {
        headers: {
          'x-api-id': 6,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.designation,
    }));
  };

  const handleInputChange = (field: keyof BasicEntry, value: any) => {
    setBasicData({
      ...basicData,
      [field]: value
    });
  };

  return (
    <Box className="edit-section">
      <Grid container spacing={4}>
        {/* First Row */}
        <Grid container spacing={8}>
          <Grid>
            <SelectField
              label="Vacancy Type"
              options={vacancyTypeOptions}
              width="360px"
              required
              name="vacancyType"
              value={basicData.vacancyType}
              onChange={(e) => handleInputChange('vacancyType', e.target.value)}
            />
          </Grid>
          <Grid>
            <TextField
              label="Vacancy Title"
              width="360px"
              required
              name="vacancyTitle"
              value={basicData.vacancyTitle}
              onChange={(e) => handleInputChange('vacancyTitle', e.target.value)}
            />
          </Grid>
          <Grid>
            <TextField
              label="Vacancy Code"
              width="360px"
              required
              name="vacancyCode"
              value={basicData.vacancyCode}
              onChange={(e) => handleInputChange('vacancyCode', e.target.value)}
            />
          </Grid>
          <Grid />
        </Grid>

        {/* Second Row */}
        <Grid container spacing={8}>
          <Grid>
            <TextField
              label="Minimum Industry Experience"
              width="360px"
              required
              type="number"
              name="minIndustryExperience"
              value={basicData.minIndustryExperience}
              onChange={(e) => handleInputChange('minIndustryExperience', e.target.value)}
            />
          </Grid>
          <Grid>
            <DatePickerField
              label="Start Date"
              value={basicData.startDate ? dayjs(basicData.startDate) : null}
              onChange={(date) => handleInputChange('startDate', date)}
              width="360px"
              required
            />
          </Grid>
          <Grid>
            <DatePickerField
              label="End Date"
              value={basicData.endDate ? dayjs(basicData.endDate) : null}
              onChange={(date) => handleInputChange('endDate', date)}
              width="360px"
              required
            />
          </Grid>
          <Grid />
        </Grid>

        {/* Third Row */}
        <Grid container spacing={8}>
          <Grid>
            <TextField
              label="Vacancy Reason"
              width="360px"
              required
              name="vacancyReason"
              value={basicData.vacancyReason}
              onChange={(e) => handleInputChange('vacancyReason', e.target.value)}
            />
          </Grid>
          <Grid>
            <AsyncAutocomplete
              label="Designation"
              width="360px"
              loadOptions={handleLoadDesignations}
              value={basicData.designation}
              onChange={(value) => handleInputChange('designation', value)}
            />
          </Grid>
          <Grid>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography component="label" className="input-field-label">
                Status
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={basicData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  name="isActive"
                  sx={{
                    color: '#0D9C33',
                    '&.Mui-checked': {
                      color: '#0D9C33',
                    },
                  }}
                />
              }
              label="Active"
              sx={{
                color: '#00000066',
                fontSize: '12px',
                fontWeight: 500,
                marginTop: '-4px',
              }}
            />
          </Grid>
          <Grid />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VacancyBasicCard; 